import React, { useState } from "react";
import { useI18n } from "bananas-commerce-admin";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";

import { Dayjs } from "dayjs";

import DateField from "../DateField";

export interface NewPriceListDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (fromDate: string, toDate: string) => void;
  isCopying?: boolean;
}

const NewPriceListDialog: React.FC<NewPriceListDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isCopying = false,
}) => {
  const { t } = useI18n();
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    setError(null);

    if (!fromDate || !toDate) {
      setError(t("Välj både start- och slutdatum"));
      return;
    }

    if (fromDate.isAfter(toDate)) {
      setError(t("Startdatum kan inte vara efter slutdatum"));
      return;
    }

    onSubmit(fromDate.format("YYYY-MM-DD"), toDate.format("YYYY-MM-DD"));
    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 8,
        },
      }}
      onClose={onClose}
    >
      <DialogTitle
        sx={{ borderBottom: 1, borderColor: "divider", pt: 4, pb: 2 }}
      >
        <Typography fontSize={24}>
          {isCopying ? t("Kopiera prislista") : t("Ny prislista")}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {isCopying
            ? t(
                "Kopierar en befintlig prislista som inkluderar alla titlar, utgivningsdagar och priser. Du kan ändra priserna efter du har kopierat prislistan.",
              )
            : t("Skapa en ny prislista")}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack gap={1} pt={2}>
          <Typography gutterBottom fontWeight={500} variant="body2">
            {t("Period")}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Stack>
              <Typography
                color="text.secondary"
                fontWeight={500}
                sx={{ mb: 1 }}
                variant="body2"
              >
                {t("Från")}
              </Typography>
              <DateField
                error={Boolean(error)}
                value={fromDate}
                onChange={(date) => setFromDate(date)}
              />
            </Stack>
            <Typography color="text.secondary" pt={4.5} variant="body2">
              —
            </Typography>
            <Stack>
              <Typography
                color="text.secondary"
                fontWeight={500}
                sx={{ mb: 1 }}
                variant="body2"
              >
                {t("Till")}
              </Typography>
              <DateField
                error={Boolean(error)}
                value={toDate}
                onChange={(date) => setToDate(date)}
              />
            </Stack>
          </Stack>
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
          <Typography color="text.secondary" variant="caption">
            {t(
              "Perioden kommer att skriva över tidigare inlagd period inom det datumspann som anges.",
            )}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>{t("Avbryt")}</Button>
        <Button
          disabled={!fromDate || !toDate}
          variant="text"
          onClick={handleSubmit}
        >
          {t("Skapa prislista")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewPriceListDialog;
